/**
 * Where a job sits in its own workflow: Capture, Review, Price, Issue.
 *
 * Every job already moves through the same four steps; nothing in the app
 * used to say which one a job was actually AT without opening it. This is
 * the one place that decision is made, so the list, the dashboard and the
 * job header can't quietly disagree about it.
 *
 * Pure. No React, no database.
 */

import { shotListFor, type ImpactPoint } from '../capture/shotlist.js';

export type Stage = 'capture' | 'review' | 'price' | 'issue';

export interface StageInput {
  shotsTaken: number;
  shotsTotal: number;
  proposalsTotal: number;
  proposalsDecided: number;
  /**
   * Accepted parts with no catalog price. Only known where the full estimate
   * has actually been built — inside an open job. A list of many jobs can't
   * cheaply know this without loading each job's whole estimate and catalog,
   * so it's left `undefined` there, and this never claims `'issue'` from data
   * it doesn't have: once review is done it reports `'price'` and stays
   * there, honest about the limit of what it knows.
   */
  missingPrices?: number;
}

export function currentStage(input: StageInput): Stage {
  // The guided shot list is a target, not a gate: "Read N photos" already
  // works on however many are taken, so a job with real proposals sitting in
  // Review has plainly left Capture, whether or not every requested angle
  // was ever shot. Only "has anything actually been read" decides that.
  if (input.proposalsTotal === 0) return 'capture';
  if (input.proposalsDecided < input.proposalsTotal) return 'review';
  if (input.missingPrices === undefined || input.missingPrices > 0) return 'price';
  return 'issue';
}

/**
 * The stage to actually show, once a job might already be issued.
 *
 * An estimator can freeze an estimate without ever completing the GUIDED
 * shot list — extra or ad-hoc photographs can cover what it asked for — so
 * `currentStage`'s shots-first gate must not relitigate that after the fact.
 * An issued job is done by definition; nothing about its capture or review
 * counts can override that.
 */
export function displayStage(input: StageInput & { issued: boolean }): Stage {
  return input.issued ? 'issue' : currentStage(input);
}

/** How many photographs this job's point of impact asks for. */
export function shotsTotalFor(impact: string): number {
  return shotListFor(impact as ImpactPoint).length;
}
